import { useState } from 'react';
import Icon from '../components/Icon.jsx';
import { adultTests, pediatricTests } from '../data.js';
import './BatterySlide.css';

function AudioBars() {
  return (
    <div className="diagnostic-wave" aria-hidden="true">
      {Array.from({ length: 13 }).map((_, index) => (
        <i key={index} style={{ '--bar': index }} />
      ))}
    </div>
  );
}

function RowPreview({ row, batteryTitle, rowNumber, position }) {
  return (
    <aside
      className={`test-row-popover test-row-popover--${position}`}
      id={`test-preview-${row.code}-${rowNumber}`}
      aria-hidden="true"
    >
      <div className="popover-glow" aria-hidden="true" />

      <div className="popover-sequence">
        <span>{String(rowNumber + 1).padStart(2, '0')}</span>
        <small>Selected procedure</small>
      </div>

      <div className="popover-content">
        <div className="popover-kicker">{batteryTitle}</div>

        <div className="popover-code-line">
          <div>
            <small>Procedure code</small>
            <strong>{row.code}</strong>
          </div>

          <div className="popover-live-chip">
            <i />
            Interactive preview
          </div>
        </div>

        <div className="popover-description-block">
          <p>{row.label}</p>

          <div className="popover-description-meta">
            <span>Clinical audiology pathway</span>
            <AudioBars />
          </div>
        </div>
      </div>

      <div className="popover-value-orbit">
        <div className="popover-value-orbit__ring" />
        <div className="popover-value-orbit__core">
          <small>Value</small>
          <strong>{row.value}</strong>
        </div>
      </div>
    </aside>
  );
}

function BatteryPanel({
  index,
  title,
  icon,
  rows,
  totalLabel,
  total,
  active,
  onActivate,
}) {
  return (
    <article
      className={`battery-panel ${active ? 'is-active' : ''}`}
      onMouseEnter={onActivate}
      onFocus={onActivate}
      onClick={onActivate}
      tabIndex="0"
    >
      <div className="battery-panel-head">
        <span>{index}</span>
        <h3>{title}</h3>
        <Icon name={icon} />
      </div>

      <div className="test-rows">
        {rows.map((row, rowIndex) => {
          const popupPosition = rowIndex < 2 ? 'below' : 'above';

          return (
            <div
              className="test-row"
              key={`${row.code}-${rowIndex}`}
              tabIndex="0"
              aria-describedby={`test-preview-${row.code}-${rowIndex}`}
            >
              <span className="test-index">
                {String(rowIndex + 1).padStart(2, '0')}
              </span>

              <b className="test-code">{row.code}</b>
              <p>{row.label}</p>
              <strong>{row.value}</strong>
              <i />

              <RowPreview
                row={row}
                batteryTitle={title}
                rowNumber={rowIndex}
                position={popupPosition}
              />
            </div>
          );
        })}

        <div className="test-total">
          <span>{totalLabel}</span>
          <b>{total}</b>
        </div>
      </div>
    </article>
  );
}

export default function BatterySlide() {
  const [focus, setFocus] = useState('adult');

  return (
    <section className="slide-content battery-slide">
      <div className="section-heading reveal-stack">
        <div className="eyebrow dark">
          <Icon name="table" />
          Audiology
        </div>

        <h2>Audiology full evaluation battery</h2>
      </div>

      <div className="battery-grid">
        <BatteryPanel
          index="01"
          title="Adults Evaluation Battery"
          icon="ear"
          rows={adultTests}
          totalLabel="Total for Adults"
          total="1200"
          active={focus === 'adult'}
          onActivate={() => setFocus('adult')}
        />

        <BatteryPanel
          index="02"
          title="Pediatric Evaluation Battery"
          icon="people"
          rows={pediatricTests}
          totalLabel="Total for Pediatric"
          total="3000"
          active={focus === 'pediatric'}
          onActivate={() => setFocus('pediatric')}
        />
      </div>
    </section>
  );
}
